import { Dialog, Transition } from '@headlessui/react'
import React, {
  Fragment,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from 'react'
import { setModal } from '../../redux/application-slice'
import { useDispatch } from 'react-redux'
import {
  useExportDocumentBlacklineSegmentsMutation,
  useGetDocumentByUuidQuery,
} from '../../redux/api-slice'
import { skipToken } from '@reduxjs/toolkit/query'
import { useParams } from 'react-router-dom'

interface ExportBlacklineOptionsModalProps {
  open: boolean
}

const ExportBlacklineOptionsModal: React.FC<
  ExportBlacklineOptionsModalProps
> = ({ open }) => {
  const [isOpen, setIsOpen] = useState<boolean>(open)
  const dispatch = useDispatch()
  const [commentsOnly, setCommentsOnly] = useState(false)
  const [exportDocumentBlacklineSegments] =
    useExportDocumentBlacklineSegmentsMutation()
  const [exporting, setExporting] = useState(false)
  const { documentId } = useParams()
  const { data: document } = useGetDocumentByUuidQuery(documentId ?? skipToken)

  useEffect(() => {
    setIsOpen(open)
  }, [open])

  const onCommentsOnlyChange = useCallback((e) => {
    setCommentsOnly(e.target.checked)
  }, [])

  const onClose = useCallback(() => {
    dispatch(setModal(null))
    setIsOpen(false)
    setCommentsOnly(false)
  }, [dispatch])

  const onExportSegments = useCallback(async () => {
    if (!document?.id) {
      return
    }
    setExporting(true)
    const exportResult = await exportDocumentBlacklineSegments({
      documentId: document?.id,
      commentsOnly,
      timezone: Intl.DateTimeFormat()
        .resolvedOptions()
        .timeZone.replaceAll('+', '%2B'),
    }).unwrap()
    window.open(exportResult.export_url, '_blank')
    setExporting(false)
    onClose()
  }, [commentsOnly, document?.id, exportDocumentBlacklineSegments, onClose])

  const nameContent = useMemo(() => {
    return (
      <Dialog.Panel className="relative transform rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all">
        <div className="flex h-full w-full items-center space-x-4">
          <div className="flex w-full flex-col justify-between space-y-2 text-sm">
            <span className="font-semibold">Export document changes</span>
            <div className="flex w-full flex-col space-y-4">
              <div className="flex h-6 items-center">
                <input
                  id="export-exclude-options"
                  aria-describedby="export-exclude-options"
                  name="export-exclude-options"
                  type="checkbox"
                  checked={commentsOnly}
                  onChange={onCommentsOnlyChange}
                  className="h-4 w-4 cursor-pointer rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                />
                <div className="ml-3 text-sm leading-6">
                  <label
                    htmlFor="export-exclude-options"
                    className="cursor-pointer font-medium text-gray-900"
                  >
                    Only include changes with comments
                  </label>
                </div>
              </div>
              <div className={'flex w-full justify-end'}>
                <button
                  type="button"
                  className={`$bg-indigo-600 inline-flex w-28 justify-center self-end rounded-md bg-indigo-500 px-3 py-2 text-sm font-semibold  text-white shadow-sm hover:bg-indigo-700  focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:col-start-2`}
                  onClick={onExportSegments}
                  disabled={exporting}
                >
                  {exporting ? 'Exporting...' : 'Export'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Dialog.Panel>
    )
  }, [commentsOnly, onCommentsOnlyChange, onExportSegments, exporting])

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog onClose={onClose} as="div" className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              {nameContent}
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

export default ExportBlacklineOptionsModal
